/**
 * The hex tiling the platform hit-tests and every renderer draws.
 * Pointy-top, offset rows, half hexes at the side edges. Coordinates normalized 0..1.
 */
/** Vertical distance between row centres, as a fraction of one hex's height. */
export const ROW_PITCH = 0.75;
/** How far the shoulder vertices sit above and below the centre, as a fraction of height. */
export const SHOULDER = 0.25;
/** How many hexes a row holds: the odd rows carry one more, halved by each edge. */
export function columnsInRow(columns, row) {
    return row % 2 === 0 ? columns : columns + 1;
}
export function cellCount(columns, rows) {
    let total = 0;
    for (let row = 0; row < rows; row += 1)
        total += columnsInRow(columns, row);
    return total;
}
/** Every cell in the order their indices run: whole rows, left to right. */
export function cells(columns, rows) {
    const found = [];
    for (let row = 0; row < rows; row += 1) {
        for (let column = 0; column < columnsInRow(columns, row); column += 1) {
            found.push({ row, column });
        }
    }
    return found;
}
export function indexOfCell(columns, rows, row, column) {
    if (row < 0 || row >= rows || column < 0 || column >= columnsInRow(columns, row))
        return null;
    let index = 0;
    for (let one = 0; one < row; one += 1)
        index += columnsInRow(columns, one);
    return index + column;
}
export function cellOfIndex(columns, rows, index) {
    if (index < 0)
        return null;
    let left = index;
    for (let row = 0; row < rows; row += 1) {
        const width = columnsInRow(columns, row);
        if (left < width)
            return { row, column: left };
        left -= width;
    }
    return null;
}
/** One hex's width and height in normalized units. */
export function hexSize(columns, rows) {
    return { width: 1 / columns, height: 1 / (ROW_PITCH * rows + SHOULDER) };
}
/** The middle of one hex. An odd row's end centres sit on the border itself. */
export function center(columns, rows, row, column) {
    const { width, height } = hexSize(columns, rows);
    const x = row % 2 === 0 ? (column + 0.5) * width : column * width;
    return { x, y: (0.5 + ROW_PITCH * row) * height };
}
/** The six corners, clockwise from the top point. Not clipped to the border. */
export function vertices(columns, rows, row, column) {
    const { width, height } = hexSize(columns, rows);
    const { x, y } = center(columns, rows, row, column);
    const half = width / 2;
    const tall = height / 2;
    const shoulder = height * SHOULDER;
    return [
        { x, y: y - tall },
        { x: x + half, y: y - shoulder },
        { x: x + half, y: y + shoulder },
        { x, y: y + tall },
        { x: x - half, y: y + shoulder },
        { x: x - half, y: y - shoulder },
    ];
}
/** Whether a point is inside one hex: the flat sides, then the four slanted ones. */
export function contains(columns, rows, row, column, x, y) {
    const { width, height } = hexSize(columns, rows);
    const at = center(columns, rows, row, column);
    const dx = Math.abs(x - at.x) / (width / 2);
    const dy = Math.abs(y - at.y) / (height / 2);
    return dx <= 1 && dy <= 1 - 0.5 * dx;
}
/** Which hex a point falls in, or null when it is outside the grid entirely. */
export function pick(columns, rows, x, y) {
    if (!(x >= 0 && x <= 1 && y >= 0 && y <= 1))
        return null;
    const { width, height } = hexSize(columns, rows);
    const about = Math.floor((y / height - 0.5) / ROW_PITCH);
    for (let row = Math.max(0, about - 1); row < Math.min(rows, about + 3); row += 1) {
        const near = Math.floor(x / width);
        const last = Math.min(columnsInRow(columns, row), near + 2);
        for (let column = Math.max(0, near - 1); column < last; column += 1) {
            if (contains(columns, rows, row, column, x, y))
                return { row, column };
        }
    }
    return nearest(columns, rows, x, y);
}
/** The closest centre, for a point on a border that no hex claimed. */
function nearest(columns, rows, x, y) {
    let best = { row: 0, column: 0 };
    let closest = Number.POSITIVE_INFINITY;
    for (const cell of cells(columns, rows)) {
        const at = center(columns, rows, cell.row, cell.column);
        const distance = (x - at.x) ** 2 + (y - at.y) ** 2;
        if (distance < closest) {
            closest = distance;
            best = cell;
        }
    }
    return best;
}
/** How many rows make the hexes regular on a display of this shape, at least one. Advice only. */
export function rowsFor(columns, width, height) {
    if (columns < 1 || width <= 0 || height <= 0)
        return 1;
    const tall = (2 * (width / columns)) / Math.sqrt(3);
    return Math.max(1, Math.round((height / tall - SHOULDER) / ROW_PITCH));
}
/** The id a cell carries everywhere: in the board's entities, in a look, and on a canvas. */
export function cellId(row, column) {
    return `cell_${String(row)}_${String(column)}`;
}
/** The cell an id names, or null for an id shaped some other way. */
export function cellFromId(id) {
    const found = /^cell_(\d+)_(\d+)$/.exec(id);
    return found === null ? null : { row: Number(found[1]), column: Number(found[2]) };
}
